import { useState, useEffect } from "react";
import { Row } from "@commonlibs";
import { Checkbox } from "@commonlibs";
import { StyledComponent } from "styled-components";
import React from "react";

interface CheckboxOption<T = string | number> {
    label: string | number;
    value: T;
}

interface BaseCheckboxProps {
    name?: string;
    label?: string | number;
    value?: boolean;
    onChange?: () => void;
    onFocus?: () => void;
    required?: boolean;
    [key: string]: unknown;
}

type CheckboxComponentType =
    | React.ComponentType<BaseCheckboxProps>
    | StyledComponent<React.ComponentType<BaseCheckboxProps>, Record<string, unknown>, BaseCheckboxProps, never>;

interface CheckboxGroupProps<T = string | number> {
    options: CheckboxOption<T>[];
    direction?: "row" | "column";
    gap?: string | number;
    defaultValue?: T[];
    value?: T[];
    onChange?: (selectedValues: T[]) => void;
    CheckboxComponent?: CheckboxComponentType;
    name?: string;
}

export const CheckboxGroup = <T extends string | number = string | number>({
    options,
    direction = "row",
    gap = 20,
    defaultValue = [],
    value: controlledValue,
    onChange,
    CheckboxComponent = Checkbox,
    name,
}: CheckboxGroupProps<T>) => {
    const [selectedValues, setSelectedValues] = useState<T[]>(defaultValue);
    const isControlled = controlledValue !== undefined;
    const currentValues = isControlled ? controlledValue : selectedValues;

    useEffect(() => {
        if (isControlled && controlledValue) {
            setSelectedValues(controlledValue);
        }
    }, [isControlled, controlledValue]);

    const handleToggle = (optionValue: T) => {
        const newValues = currentValues.includes(optionValue)
            ? currentValues.filter((v) => v !== optionValue)
            : [...currentValues, optionValue];

        if (!isControlled) {
            setSelectedValues(newValues);
        }

        onChange?.(newValues);
    };

    return (
        <Row
            css={{
                flexDirection: direction,
                gap,
                paddingBottom: "2px",
                flexWrap: "wrap",
                rowGap: typeof gap === "number" ? `${gap * 0.6}px` : gap,
                columnGap: gap,
            }}
        >
            {options.map((option) => {
                const isChecked = currentValues.includes(option.value);
                return (
                    <CheckboxComponent
                        key={String(option.value)}
                        name={name}
                        label={String(option.label)}
                        value={isChecked}
                        onChange={() => handleToggle(option.value)}
                    />
                );
            })}
        </Row>
    );
};


